
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import Header from "../../components/Header/Header";

import {
    createDirectChat,
    createRoomChat,
    fetchMyChats,
    getOrCreateSavedChat,
    setActiveChatIdLocal,
} from "../../actions/chatActions";
import { forwardMessagesBulk } from "../../actions/message";
import { getUsers } from "../../actions/user.actions";
import { startCall } from "../../actions/call.actions";
import axios from "../../helpers/axios";
import { callService, refreshCallsAuth } from "../../calls";
import { unlockAudio } from "../../utils/audioUnlock";
import { getChatDraft, subscribeChatDrafts } from "../../utils/chatDrafts";

import ChatCore from "./ChatCore";
import ForwardMessageModal from "./ForwardMessageModal";
import OfficeChatTabs from "./OfficeChatTabs";

const getId = (value) => String(value?._id || value?.id || value || "").trim();

const getUserName = (user) => {
    if (!user) return "Пользователь";
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return name || user.email || user.username || "Пользователь";
};

const normalizeSearch = (value) => (
    String(value || "")
        .toLocaleLowerCase("ru-RU")
        .replace(/\s+/g, " ")
        .trim()
);

const getUnread = (chat) => (
    Number(chat?.unreadCount || 0) + Number(chat?.missedCount || 0)
);

const getBadge = (value) => {
    const count = Number(value || 0);
    if (count <= 0) return "";
    return count > 99 ? "99+" : String(count);
};

const getChatTitle = (chat, currentUserId) => {
    if (!chat) return "Чат";
    if (chat.chatType === "saved") return chat.title || "Сохранённое";
    if (chat.chatType === "scene_guest") return chat.title || "Чат по 3D-сцене";
    if (chat.entity?.type && chat.entity.type !== "none") {
        return chat.entity.title || chat.title || "Обсуждение";
    }
    if (chat.isGroup || chat.chatType === "group") return chat.title || "Общий чат";
    if (chat.user) return getUserName(chat.user);

    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    const peer = participants.find((item) => getId(item) !== getId(currentUserId));

    return getUserName(peer);
};

const getChatSubtitle = (chat, currentUserId) => {
    if (!chat) return "";
    if (chat.chatType === "saved") return "Личное хранилище сообщений";
    if (chat.chatType === "scene_guest") return chat.guestAccess?.customerName || "Заказчик без регистрации";
    if (chat.entity?.type === "product") return chat.entity.subtitle || "Обсуждение товара";
    if (chat.entity?.type === "order") return chat.entity.subtitle || "Обсуждение заказа";
    if (chat.chatType === "group" || chat.isGroup) {
        return `${chat.participants?.length || 0} участников`;
    }

    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    const peer = participants.find((item) => getId(item) !== getId(currentUserId));

    return peer?.email || chat.user?.email || "Личный диалог";
};

const formatCallStatus = (status) => {
    switch (status) {
        case "ringing": return "вызов";
        case "accepted": return "принят";
        case "missed": return "пропущен";
        case "ended": return "завершён";
        case "rejected": return "отклонён";
        case "cancelled": return "отменён";
        case "busy": return "занято";
        case "failed": return "ошибка";
        default: return status || "";
    }
};

const formatPreview = (lastMessage, currentUserId) => {
    if (!lastMessage) return "Сообщений пока нет";

    const sender = getId(lastMessage.senderId || lastMessage.sender);
    const prefix = sender && sender === getId(currentUserId) ? "Вы: " : "";

    if (lastMessage.messageKind === "product_card" || lastMessage.entity?.type === "product") {
        return `${prefix}Товар: ${lastMessage.entity?.title || "карточка товара"}`;
    }
    if (lastMessage.messageKind === "order_card" || lastMessage.entity?.type === "order") {
        return `${prefix}Заказ: ${lastMessage.entity?.title || "карточка заказа"}`;
    }
    if (lastMessage.type === "call" || lastMessage.call) {
        const call = lastMessage.call || {};
        return `${prefix}${call.video ? "видеозвонок" : "звонок"}${call.status ? ` · ${formatCallStatus(call.status)}` : ""}`;
    }
    if (lastMessage.fileName || lastMessage.fileUrl) {
        return `${prefix}Вложение${lastMessage.fileName ? ` · ${lastMessage.fileName}` : ""}`;
    }

    return `${prefix}${lastMessage.text || "Сообщение"}`;
};

/**
 * Галочки у последнего сообщения в списке чатов, как в приложении и Telegram:
 * одна — доставлено, две — прочитано. Только у своих сообщений (не звонков).
 */
const getLastMessageStatus = (lastMessage, currentUserId) => {
    if (!lastMessage || lastMessage.type === "call" || lastMessage.call) return "";
    const me = getId(currentUserId);
    const sender = getId(lastMessage.senderId || lastMessage.sender);
    if (!me || sender !== me) return "";
    const readBy = (Array.isArray(lastMessage.readBy) ? lastMessage.readBy : []).map(getId);
    return readBy.some((id) => id && id !== me) ? "read" : "sent";
};

/** Время последнего сообщения: сегодня — часы, на этой неделе — день, раньше — дата. */
const formatListTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    }
    const days = (now - date) / 86400000;
    if (days < 6) return date.toLocaleDateString("ru-RU", { weekday: "short" });
    return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

function ReadMark({ status }) {
    if (!status) return null;
    const read = status === "read";
    return (
        <span
            className={`office-chat-AO__read is-${status}`}
            title={read ? "Прочитано" : "Доставлено"}
            aria-label={read ? "Прочитано" : "Доставлено"}
            style={{ color: read ? "#4ea8ff" : "rgba(160,170,190,0.85)", fontSize: 13, fontWeight: 700, letterSpacing: read ? "-4px" : 0, marginRight: read ? 4 : 0 }}
        >
            {read ? "✓✓" : "✓"}
        </span>
    );
}

const filterChatsByTab = (chats, tab) => (
    (chats || []).filter((chat) => {
        if (!chat) return false;
        if (tab === "direct") return chat.chatType === "direct";
        if (tab === "group") return ["group", "scene_guest"].includes(chat.chatType);
        if (tab === "product") return chat.chatType === "product_discussion" || chat.entity?.type === "product";
        if (tab === "order") return chat.chatType === "order_discussion" || chat.entity?.type === "order";
        return true;
    })
);

const buildTabStat = (chats) => ({
    total: chats.length,
    unread: chats.reduce((sum, chat) => sum + getUnread(chat), 0),
});

const chatMatchesSearch = (chat, query, currentUserId) => {
    const clean = normalizeSearch(query);
    if (!clean) return true;

    const participants = Array.isArray(chat?.participants) ? chat.participants : [];
    const haystack = normalizeSearch([
        getChatTitle(chat, currentUserId),
        getChatSubtitle(chat, currentUserId),
        formatPreview(chat?.lastMessage, currentUserId),
        chat?.entity?.title,
        chat?.entity?.subtitle,
        ...participants.flatMap((user) => [getUserName(user), user?.email]),
    ].filter(Boolean).join(" "));

    return haystack.includes(clean);
};

const userMatchesSearch = (user, query) => {
    const clean = normalizeSearch(query);
    if (!clean) return true;

    return normalizeSearch([
        getUserName(user),
        user?.email,
        user?.username,
        user?.contactNumber,
        user?.role,
    ].filter(Boolean).join(" ")).includes(clean);
};

const callMatchesSearch = (call, query) => {
    const clean = normalizeSearch(query);
    if (!clean) return true;

    return normalizeSearch([
        call?.peerName,
        call?.peerEmail,
        call?.status,
        call?.direction,
        call?.video ? "видео" : "аудио",
    ].filter(Boolean).join(" ")).includes(clean);
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDuration = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds || 0)));
    const min = Math.floor(total / 60);
    return `${min}:${String(total % 60).padStart(2, "0")}`;
};

const getLastSeenText = (userId, onlineIds, lastSeen) => {
    const id = getId(userId);
    if (onlineIds.has(id)) return "онлайн";

    const value = lastSeen?.[id];
    if (!value) return "не в сети";

    const time = new Date(value).getTime();
    if (!Number.isFinite(time)) return "не в сети";

    const sec = Math.max(0, Math.floor((Date.now() - time) / 1000));
    if (sec < 60) return "был(а) только что";
    if (sec < 3600) return `был(а) ${Math.floor(sec / 60)} мин назад`;
    if (sec < 86400) return `был(а) ${Math.floor(sec / 3600)} ч назад`;
    return `был(а) ${Math.floor(sec / 86400)} дн назад`;
};

export default function AdminMessenger({ initialSection = "chats" }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { chatId = "" } = useParams();

    const { user: me } = useSelector((state) => state.auth || {}, shallowEqual);
    const { chats = [], loading = false, activeChatId = null, error = null } = useSelector(
        (state) => state.chat || {},
        shallowEqual
    );
    const users = useSelector((state) => state.user?.users || [], shallowEqual);

    const currentUserId = me?._id || me?.id || null;

    // Черновики: список чатов показывает «Черновик: …» (utils/chatDrafts.js).
    const [, setDraftsVersion] = useState(0);
    useEffect(() => subscribeChatDrafts(() => setDraftsVersion((value) => value + 1)), []);
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const querySection = query.get("section");
    const focusMessageId = query.get("message");
    const section = location.pathname === "/calls"
        ? "calls"
        : ["chats", "contacts", "calls", "settings"].includes(querySection)
            ? querySection
            : initialSection;

    const [activeTab, setActiveTab] = useState("all");
    const [notificationPermission, setNotificationPermission] = useState(() => (
        typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission : 'unsupported'
    ));
    const [search, setSearch] = useState("");
    const [overlaySearch, setOverlaySearch] = useState("");
    const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
    const [mobileContentOpen, setMobileContentOpen] = useState(!!chatId);
    const sidebarMenuRef = useRef(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createMode, setCreateMode] = useState("direct");
    const [createSearch, setCreateSearch] = useState("");
    const [createGroupTitle, setCreateGroupTitle] = useState("Общий чат");
    const [createGroupIds, setCreateGroupIds] = useState([]);
    const [createBusy, setCreateBusy] = useState("");
    const [createError, setCreateError] = useState("");

    const [forwardMessagesDraft, setForwardMessagesDraft] = useState([]);
    const [forwardSearch, setForwardSearch] = useState("");
    const [forwardSelectedChatIds, setForwardSelectedChatIds] = useState([]);
    const [forwardingBulk, setForwardingBulk] = useState(false);
    const [forwardError, setForwardError] = useState("");
    const [forwardNotice, setForwardNotice] = useState("");
    const forwardCompleteRef = useRef(null);

    const [onlineIds, setOnlineIds] = useState(new Set());
    const [lastSeen, setLastSeen] = useState({});
    const [calls, setCalls] = useState([]);
    const [callsLoading, setCallsLoading] = useState(false);

    const selectedChat = useMemo(() => {
        const id = chatId || activeChatId;
        if (!id) return null;
        return chats.find((chat) => getId(chat?._id) === getId(id)) || null;
    }, [activeChatId, chatId, chats]);

    const contacts = useMemo(() => (
        (Array.isArray(users) ? users : [])
            .filter((user) => getId(user) && getId(user) !== getId(currentUserId))
            .filter((user) => userMatchesSearch(user, overlaySearch))
            .sort((a, b) => {
                const aOnline = onlineIds.has(getId(a)) ? 0 : 1;
                const bOnline = onlineIds.has(getId(b)) ? 0 : 1;
                if (aOnline !== bOnline) return aOnline - bOnline;
                return getUserName(a).localeCompare(getUserName(b), "ru");
            })
    ), [currentUserId, onlineIds, overlaySearch, users]);

    const createContacts = useMemo(() => (
        (Array.isArray(users) ? users : [])
            .filter((user) => getId(user) && getId(user) !== getId(currentUserId))
            .filter((user) => userMatchesSearch(user, createSearch))
    ), [createSearch, currentUserId, users]);

    const visibleChats = useMemo(() => (
        filterChatsByTab(chats, activeTab)
            .filter((chat) => chatMatchesSearch(chat, search, currentUserId))
    ), [activeTab, chats, currentUserId, search]);

    const visibleCalls = useMemo(() => (
        (calls || []).filter((call) => callMatchesSearch(call, overlaySearch))
    ), [calls, overlaySearch]);

    const totalUnread = useMemo(() => chats.reduce(
        (sum, chat) => sum + Math.max(0, Number(chat?.unreadCount || 0)), 0
    ), [chats]);

    const requestBrowserNotifications = useCallback(async () => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            setNotificationPermission('unsupported');
            return;
        }
        try { setNotificationPermission(await Notification.requestPermission()); }
        catch (_error) { setNotificationPermission(Notification.permission); }
    }, []);

    const tabCounts = useMemo(() => {
        const direct = filterChatsByTab(chats, "direct");
        const group = filterChatsByTab(chats, "group");
        const product = filterChatsByTab(chats, "product");
        const order = filterChatsByTab(chats, "order");

        return {
            all: buildTabStat(chats),
            direct: buildTabStat(direct),
            group: buildTabStat(group),
            product: buildTabStat(product),
            order: buildTabStat(order),
        };
    }, [chats]);

    const loadCalls = useCallback(async () => {
        setCallsLoading(true);

        try {
            const { data } = await axios.get("/call");
            setCalls(Array.isArray(data?.calls) ? data.calls : []);
        } catch (_error) {
            setCalls([]);
        } finally {
            setCallsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!currentUserId) return;

        dispatch(fetchMyChats({ context: "all", type: "all" }));
        dispatch(getUsers("", undefined, 0, 100));
    }, [currentUserId, dispatch]);

    useEffect(() => {
        if (section === "calls") loadCalls();
        setMobileContentOpen(!!chatId);
    }, [chatId, loadCalls, section]);

    useEffect(() => {
        if (chatId) dispatch(setActiveChatIdLocal(chatId));
    }, [chatId, dispatch]);

    useEffect(() => {
        if (!sidebarMenuOpen) return undefined;

        const onPointer = (event) => {
            if (!sidebarMenuRef.current?.contains(event.target)) setSidebarMenuOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setSidebarMenuOpen(false);
        };

        document.addEventListener("pointerdown", onPointer);
        document.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("pointerdown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [sidebarMenuOpen]);

    useEffect(() => {
        refreshCallsAuth();

        const socket = callService?.socket;
        if (!socket) return undefined;

        const onState = ({ online } = {}) => setOnlineIds(new Set((online || []).map(getId)));
        const onOnline = ({ userId } = {}) => {
            const id = getId(userId);
            if (!id) return;
            setOnlineIds((prev) => new Set([...prev, id]));
        };
        const onOffline = ({ userId } = {}) => {
            const id = getId(userId);
            if (!id) return;
            setOnlineIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        };

        socket.on("presence:state", onState);
        socket.on("presence:online", onOnline);
        socket.on("presence:offline", onOffline);

        // presence:heartbeat на BACK не обрабатывается ни в одном namespace:
        // /calls держит онлайн по connect/disconnect, а Socket.IO сам шлёт
        // ping каждые 25 сек (pingInterval в socketServer.js).
        // Таймер каждые 30 сек отправлял событие в никуда.

        return () => {
            socket.off("presence:state", onState);
            socket.off("presence:online", onOnline);
            socket.off("presence:offline", onOffline);
        };
    }, []);

    // Кто в сети — для шапки чата и профиля участника.
    const presenceFor = useCallback((id) => {
        const key = getId(id);
        return { online: onlineIds.has(key), lastSeenAt: lastSeen?.[key] || null };
    }, [onlineIds, lastSeen]);

    useEffect(() => {
        if (!currentUserId) return undefined;

        let cancelled = false;

        axios.get("/admin/presence")
            .then(({ data }) => {
                if (!cancelled) setLastSeen(data?.lastSeen || {});
            })
            .catch(() => {
                if (!cancelled) setLastSeen({});
            });

        return () => {
            cancelled = true;
        };
    }, [currentUserId]);

    const getBaseChatPath = useCallback(() => {
        const selectedId = getId(selectedChat?._id);
        return selectedId ? `/chat/${selectedId}` : "/chat";
    }, [selectedChat?._id]);

    const closeOverlay = useCallback(() => {
        setSidebarMenuOpen(false);
        setOverlaySearch("");
        navigate(getBaseChatPath());
    }, [getBaseChatPath, navigate]);

    const navigateSection = useCallback((next) => {
        setSidebarMenuOpen(false);
        setOverlaySearch("");

        if (next === "chats") {
            closeOverlay();
            return;
        }

        const basePath = getBaseChatPath();
        navigate(`${basePath}?section=${encodeURIComponent(next)}`);

        if (next === "calls") {
            loadCalls();
        }
    }, [closeOverlay, getBaseChatPath, loadCalls, navigate]);

    useEffect(() => {
        if (!["contacts", "calls", "settings"].includes(section)) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") closeOverlay();
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [closeOverlay, section]);

    const selectChat = useCallback((chat) => {
        const id = getId(chat?._id);
        if (!id) return;

        setMobileContentOpen(true);
        dispatch(setActiveChatIdLocal(id));
        navigate(`/chat/${id}`);
    }, [dispatch, navigate]);

    const backToList = useCallback(() => {
        setMobileContentOpen(false);
        dispatch(setActiveChatIdLocal(null));
        navigate("/chat");
    }, [dispatch, navigate]);

    const openSaved = useCallback(async () => {
        setSidebarMenuOpen(false);

        const result = await dispatch(getOrCreateSavedChat());
        const chat = result?.payload?.chat;

        if (!chat?._id) return;

        setActiveTab("all");
        setSearch("");
        selectChat(chat);
        dispatch(fetchMyChats({ context: "all", type: "all" }));
    }, [dispatch, selectChat]);

    const ensureDirectChat = useCallback(async (peerId) => {
        const id = getId(peerId);
        if (!id) return null;

        const existing = chats.find((chat) => {
            if (chat?.chatType !== "direct" || chat?.isGroup) return false;
            const participants = Array.isArray(chat.participants) ? chat.participants : [];
            return participants.some((user) => getId(user) === id);
        });

        if (existing?._id) return existing;

        const result = await dispatch(createDirectChat(id));
        return result?.payload?.chat || null;
    }, [chats, dispatch]);

    const openContactChat = useCallback(async (contact) => {
        const peerId = getId(contact);
        if (!peerId || createBusy) return;

        setCreateBusy(peerId);
        setCreateError("");

        try {
            const chat = await ensureDirectChat(peerId);

            if (!chat?._id) {
                setCreateError("Не удалось открыть личный чат.");
                return;
            }

            setCreateOpen(false);
            selectChat(chat);
        } finally {
            setCreateBusy("");
        }
    }, [createBusy, ensureDirectChat, selectChat]);

    const placeCall = useCallback(async (contact, video) => {
        const peerId = getId(contact);
        if (!peerId || createBusy) return;

        setCreateBusy(peerId);
        setCreateError("");

        try {
            const chat = await ensureDirectChat(peerId);

            if (!chat?._id) {
                setCreateError("Для звонка не удалось открыть личный чат.");
                return;
            }

            await unlockAudio().catch(() => {});
            dispatch(startCall({
                to: peerId,
                video: !!video,
                chatId: chat._id,
            }));
        } finally {
            setCreateBusy("");
        }
    }, [createBusy, dispatch, ensureDirectChat]);

    const toggleGroupMember = useCallback((userId) => {
        const id = getId(userId);
        if (!id || createBusy) return;

        setCreateError("");
        setCreateGroupIds((prev) => (
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id]
        ));
    }, [createBusy]);

    const createGroup = useCallback(async (event) => {
        event?.preventDefault?.();

        const title = createGroupTitle.trim();
        const participants = Array.from(new Set(createGroupIds.map(getId).filter(Boolean)));

        if (!title) {
            setCreateError("Введите название группы.");
            return;
        }
        if (!participants.length) {
            setCreateError("Выберите хотя бы одного участника.");
            return;
        }

        setCreateBusy("group");
        setCreateError("");

        try {
            const result = await dispatch(createRoomChat({ title, participants }));
            const chat = result?.payload?.chat;

            if (!chat?._id) {
                setCreateError(result?.error || "Не удалось создать групповой чат.");
                return;
            }

            setCreateOpen(false);
            setCreateMode("direct");
            setCreateSearch("");
            setCreateGroupTitle("Общий чат");
            setCreateGroupIds([]);
            selectChat(chat);
            dispatch(fetchMyChats({ context: "all", type: "all" }));
        } finally {
            setCreateBusy("");
        }
    }, [createGroupIds, createGroupTitle, dispatch, selectChat]);

    const requestForward = useCallback((items, options = {}) => {
        const normalized = (Array.isArray(items) ? items : [items])
            .filter((message) => message?._id && !String(message._id).startsWith("local:"))
            .slice(0, 100);

        if (!normalized.length) return;

        setForwardMessagesDraft(normalized);
        setForwardSearch("");
        setForwardSelectedChatIds([]);
        setForwardError("");
        forwardCompleteRef.current = typeof options?.onComplete === "function"
            ? options.onComplete
            : null;
    }, []);

    const closeForward = useCallback(() => {
        if (forwardingBulk) return;
        setForwardMessagesDraft([]);
        setForwardSearch("");
        setForwardSelectedChatIds([]);
        setForwardError("");
        forwardCompleteRef.current = null;
    }, [forwardingBulk]);

    const toggleForwardChat = useCallback((chat) => {
        const id = getId(chat?._id);
        if (!id || forwardingBulk) return;

        setForwardSelectedChatIds((prev) => {
            if (prev.includes(id)) return prev.filter((item) => item !== id);
            if (prev.length >= 10) {
                setForwardError("Можно выбрать максимум 10 чатов.");
                return prev;
            }
            setForwardError("");
            return [...prev, id];
        });
    }, [forwardingBulk]);

    const submitForward = useCallback(async () => {
        if (forwardingBulk || !forwardMessagesDraft.length || !forwardSelectedChatIds.length) return;

        const messageIds = forwardMessagesDraft.map((message) => getId(message?._id)).filter(Boolean);
        const targetChatIds = forwardSelectedChatIds.map(getId).filter(Boolean);

        setForwardingBulk(true);
        setForwardError("");

        const result = await dispatch(forwardMessagesBulk(messageIds, targetChatIds));

        if (!result || result.ok === false) {
            setForwardingBulk(false);
            setForwardError("Не удалось переслать сообщения.");
            return;
        }

        setForwardingBulk(false);
        setForwardMessagesDraft([]);
        setForwardSearch("");
        setForwardSelectedChatIds([]);
        setForwardNotice("Сообщения пересланы");

        forwardCompleteRef.current?.();
        forwardCompleteRef.current = null;
    }, [dispatch, forwardMessagesDraft, forwardSelectedChatIds, forwardingBulk]);

    useEffect(() => {
        if (!forwardNotice) return undefined;
        const timer = window.setTimeout(() => setForwardNotice(""), 2200);
        return () => window.clearTimeout(timer);
    }, [forwardNotice]);

    const openForwardOrigin = useCallback((origin) => {
        const sourceChatId = getId(origin?.chatId);
        const sourceMessageId = getId(origin?.messageId);

        if (!sourceChatId || !sourceMessageId) return false;
        if (!chats.some((chat) => getId(chat?._id) === sourceChatId)) return false;

        setActiveTab("all");
        setSearch("");
        dispatch(setActiveChatIdLocal(sourceChatId));
        navigate(`/chat/${sourceChatId}?message=${encodeURIComponent(sourceMessageId)}`);
        return true;
    }, [chats, dispatch, navigate]);

    const renderChatList = () => {
        if (loading && !chats.length) {
            return <div className="office-chat-AO__hint">Загрузка чатов…</div>;
        }

        if (!visibleChats.length) {
            return (
                <div className="office-chat-AO__empty-list">
                    <div className="office-chat-AO__empty-title">
                        {search ? "Ничего не найдено" : "Чатов пока нет"}
                    </div>
                    <div className="office-chat-AO__empty-text">
                        {search ? "Попробуйте другой запрос." : "Создайте личный или групповой чат через меню."}
                    </div>
                </div>
            );
        }

        return visibleChats.map((chat) => {
            const active = getId(selectedChat?._id) === getId(chat?._id);
            const badge = getBadge(getUnread(chat));
            const title = getChatTitle(chat, currentUserId);

            return (
                <button
                    type="button"
                    key={chat._id}
                    className={`office-chat-AO__chat ${active ? "is-active" : ""}`}
                    onClick={() => selectChat(chat)}
                >
                    <div className="office-chat-AO__avatar">{title.slice(0, 1).toUpperCase()}</div>
                    <div className="office-chat-AO__chat-main">
                        <div className="office-chat-AO__chat-head">
                            <span>{title}</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", flexShrink: 0 }}>
                                <ReadMark status={getLastMessageStatus(chat?.lastMessage, currentUserId)} />
                                {formatListTime(chat?.lastMessageAt || chat?.lastMessage?.createdAt) ? (
                                    <small style={{ opacity: 0.6, fontSize: 11, fontWeight: 500 }}>
                                        {formatListTime(chat?.lastMessageAt || chat?.lastMessage?.createdAt)}
                                    </small>
                                ) : null}
                                {!!badge && <b className="office-chat-AO__badge">{badge}</b>}
                            </span>
                        </div>
                        <div className="office-chat-AO__chat-sub">{getChatSubtitle(chat, currentUserId)}</div>
                        {(() => {
                            const draft = active ? "" : getChatDraft(currentUserId, chat._id).replace(/\s+/g, " ").trim();
                            return draft ? (
                                <div className="office-chat-AO__chat-preview">
                                    <span style={{ color: "#e53935", fontWeight: 600 }}>Черновик: </span>{draft}
                                </div>
                            ) : (
                                <div className="office-chat-AO__chat-preview">{formatPreview(chat?.lastMessage, currentUserId)}</div>
                            );
                        })()}
                    </div>
                </button>
            );
        });
    };

    const renderContacts = () => (
        <div className="admin-messenger-AO__section admin-messenger-AO__section--modal">
            <div className="admin-messenger-AO__contacts">
                {contacts.map((contact) => {
                    const id = getId(contact);
                    const online = onlineIds.has(id);
                    const busy = createBusy === id;

                    return (
                        <div className="admin-messenger-AO__contact" key={id}>
                            <div className="admin-messenger-AO__contact-avatar">
                                {getUserName(contact).slice(0, 1).toUpperCase()}
                                <span className={online ? "is-online" : ""} />
                            </div>
                            <div className="admin-messenger-AO__contact-main">
                                <b>{getUserName(contact)}</b>
                                <span>{contact?.email || contact?.role || "Пользователь"}</span>
                                <small>{getLastSeenText(id, onlineIds, lastSeen)}</small>
                            </div>
                            <div className="admin-messenger-AO__contact-actions">
                                <button type="button" onClick={() => openContactChat(contact)} disabled={busy}>
                                    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 19 }} />
                                    Чат
                                </button>
                                <button type="button" onClick={() => placeCall(contact, false)} disabled={busy}>
                                    <PhoneOutlinedIcon sx={{ fontSize: 19 }} />
                                    Аудио
                                </button>
                                <button type="button" onClick={() => placeCall(contact, true)} disabled={busy}>
                                    <span aria-hidden="true">▣</span>
                                    Видео
                                </button>
                            </div>
                        </div>
                    );
                })}

                {!contacts.length && (
                    <div className="admin-messenger-AO__empty">
                        {overlaySearch ? "Контактов по этому запросу нет." : "Контактов пока нет."}
                    </div>
                )}
            </div>

            {!!createError && <div className="admin-messenger-AO__error">{createError}</div>}
        </div>
    );

    const renderCalls = () => (
        <div className="admin-messenger-AO__section admin-messenger-AO__section--modal">
            <div className="admin-messenger-AO__modal-toolbar">
                <span>{visibleCalls.length ? `Найдено: ${visibleCalls.length}` : "История вызовов"}</span>
                <button type="button" onClick={loadCalls} disabled={callsLoading}>
                    <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                    {callsLoading ? "Обновляем…" : "Обновить"}
                </button>
            </div>

            <div className="admin-messenger-AO__calls">
                {visibleCalls.map((call) => (
                    <div className="admin-messenger-AO__call" key={call._id}>
                        <div className="admin-messenger-AO__contact-avatar">
                            {(call?.peerName || call?.peerEmail || "К").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="admin-messenger-AO__contact-main">
                            <b>{call?.peerName || call?.peerEmail || "Контакт"}</b>
                            <span>
                                {call?.direction === "outgoing" ? "Исходящий" : "Входящий"} · {call?.video ? "видео" : "аудио"} · {formatCallStatus(call?.status) || "—"}
                            </span>
                            <small>{formatDateTime(call?.startedAt || call?.createdAt)} · {formatDuration(call?.durationSec)}</small>
                        </div>
                    </div>
                ))}

                {!callsLoading && !visibleCalls.length && (
                    <div className="admin-messenger-AO__empty">
                        {overlaySearch ? "Звонков по этому запросу нет." : "История звонков пока пустая."}
                    </div>
                )}
            </div>
        </div>
    );

    const renderSettings = () => (
        <div className="admin-messenger-AO__section admin-messenger-AO__section--modal">
            <div className="admin-messenger-AO__settings-grid">
                <div className="admin-messenger-AO__settings-card">
                    <b>Уведомления</b>
                    <span>{notificationPermission === 'granted'
                        ? 'Уведомления браузера разрешены. Новые сообщения будут показываться, когда чат не в фокусе.'
                        : notificationPermission === 'denied'
                            ? 'Браузер блокирует уведомления. Разрешите их в настройках сайта.'
                            : notificationPermission === 'unsupported'
                                ? 'Этот браузер не поддерживает системные уведомления.'
                                : 'Разрешите уведомления, чтобы видеть новые сообщения вне вкладки.'}</span>
                    {notificationPermission === 'default' && <button type="button" onClick={requestBrowserNotifications}>Разрешить уведомления</button>}
                </div>
                <div className="admin-messenger-AO__settings-card">
                    <b>Звук и звонки</b>
                    <span>Выбор устройств, рингтон и дополнительные параметры звонков будут жить в этой модалке.</span>
                </div>
                <div className="admin-messenger-AO__settings-card">
                    <b>Оформление</b>
                    <span>Фон конкретного чата уже настраивается из меню самого диалога.</span>
                </div>
            </div>
        </div>
    );

    const renderContent = () => {
        if (selectedChat) {
            const officeId = selectedChat?.officeId?._id || selectedChat?.officeId || null;

            return (
                <ChatCore
                    key={getId(selectedChat._id)}
                    chat={selectedChat}
                    officeId={officeId}
                    chats={chats}
                    focusMessageId={focusMessageId}
                    realtimeManaged={true}
                    onForwardRequest={requestForward}
                    onOpenForwardOrigin={openForwardOrigin}
                    onBack={backToList}
                    presenceFor={presenceFor}
                />
            );
        }

        return (
            <div className="office-chat-AO__empty office-chat-AO__empty--content">
                <div className="office-chat-AO__empty-title">Выберите чат</div>
                <div className="office-chat-AO__empty-text">
                    Список диалогов всегда находится слева, выбранный чат открывается справа.
                </div>
            </div>
        );
    };

    const overlaySection = ["contacts", "calls", "settings"].includes(section)
        ? section
        : null;

    const overlayMeta = overlaySection === "contacts"
        ? { title: "Контакты", subtitle: "Чаты и звонки с пользователями", count: contacts.length }
        : overlaySection === "calls"
            ? { title: "Звонки", subtitle: "Входящие, исходящие и пропущенные вызовы", count: visibleCalls.length }
            : overlaySection === "settings"
                ? { title: "Настройки", subtitle: "Параметры мессенджера", count: null }
                : null;

    const overlayContent = overlaySection === "contacts"
        ? renderContacts()
        : overlaySection === "calls"
            ? renderCalls()
            : overlaySection === "settings"
                ? renderSettings()
                : null;

    return (
        <div className="admin-messenger-page-AO">
            <Header />

            <main className="admin-messenger-AO">
                <div className="admin-messenger-AO__topbar">
                    <button type="button" onClick={() => navigate("/")}>
                        <span aria-hidden="true">←</span>
                        Назад в админку
                    </button>
                    <div>
                        <b>Мессенджер</b>
                        <span>Чаты, контакты и звонки · непрочитанных: {totalUnread}</span>
                    </div>
                </div>

                <div className={`office-chat-AO ${mobileContentOpen ? "is-chat-open" : ""}`}>
                <div className="office-chat-AO__layout">
                    <aside className="office-chat-AO__sidebar">
                        <div className="office-chat-AO__sidebar-head" ref={sidebarMenuRef}>
                            <button
                                type="button"
                                className={`office-chat-AO__burger ${sidebarMenuOpen ? "is-open" : ""}`}
                                onClick={() => setSidebarMenuOpen((state) => !state)}
                                title="Меню чатов"
                                aria-label="Меню чатов"
                                aria-expanded={sidebarMenuOpen}
                            >
                                <MenuRoundedIcon sx={{ fontSize: 22 }} />
                            </button>

                            <label className="office-chat-AO__global-search">
                                <SearchRoundedIcon sx={{ fontSize: 19 }} />
                                <input
                                    type="search"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Поиск по всем чатам"
                                    aria-label="Поиск"
                                />
                                {!!search && (
                                    <button type="button" onClick={() => setSearch("")} aria-label="Очистить поиск">
                                        <CloseRoundedIcon sx={{ fontSize: 17 }} />
                                    </button>
                                )}
                            </label>

                            {sidebarMenuOpen && (
                                <div className="office-chat-AO__burger-menu">
                                    <div className="office-chat-AO__burger-actions">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSidebarMenuOpen(false);
                                                setCreateOpen(true);
                                                setCreateMode("direct");
                                                setCreateError("");
                                            }}
                                        >
                                            <AddCommentOutlinedIcon sx={{ fontSize: 19 }} />
                                            <span>Создать чат</span>
                                        </button>

                                        <button type="button" onClick={() => navigateSection("chats")}>
                                            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 19 }} />
                                            <span>Чаты</span>
                                            {tabCounts.all.unread > 0 && <small>{getBadge(tabCounts.all.unread)}</small>}
                                        </button>

                                        <button type="button" onClick={() => navigateSection("contacts")}>
                                            <ContactsOutlinedIcon sx={{ fontSize: 19 }} />
                                            <span>Контакты</span>
                                        </button>

                                        <button type="button" onClick={() => navigateSection("calls")}>
                                            <PhoneOutlinedIcon sx={{ fontSize: 19 }} />
                                            <span>Звонки</span>
                                        </button>

                                        <button type="button" onClick={openSaved}>
                                            <BookmarkBorderOutlinedIcon sx={{ fontSize: 19 }} />
                                            <span>Сохранённое</span>
                                        </button>

                                        <button type="button" onClick={() => navigateSection("settings")}>
                                            <SettingsOutlinedIcon sx={{ fontSize: 19 }} />
                                            <span>Настройки</span>
                                        </button>
                                    </div>

                                    <OfficeChatTabs
                                        variant="menu"
                                        value={activeTab}
                                        onChange={(next) => {
                                            setActiveTab(next);
                                            setSidebarMenuOpen(false);
                                            if (section !== "chats") navigateSection("chats");
                                        }}
                                        counts={tabCounts}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="admin-messenger-AO__sidebar-title">
                            <span>Чаты</span>
                            <b>{visibleChats.length}</b>
                        </div>

                        {error && <div className="admin-messenger-AO__side-error">{error}</div>}

                        <div className="office-chat-AO__sidebar-list">
                            {renderChatList()}
                        </div>
                    </aside>

                    <section className="office-chat-AO__content">
                        {renderContent()}
                    </section>
                </div>

                <ForwardMessageModal
                    open={forwardMessagesDraft.length > 0}
                    messages={forwardMessagesDraft}
                    chats={chats}
                    currentUserId={currentUserId}
                    sourceChatId={selectedChat?._id || null}
                    query={forwardSearch}
                    onQueryChange={(value) => {
                        setForwardSearch(value);
                        setForwardError("");
                    }}
                    selectedChatIds={forwardSelectedChatIds}
                    maxChats={10}
                    busy={forwardingBulk}
                    progressText={forwardingBulk ? "Пересылаем сообщения…" : ""}
                    error={forwardError}
                    onClose={closeForward}
                    onToggleChat={toggleForwardChat}
                    onSubmit={submitForward}
                />

                {!!forwardNotice && (
                    <div className="office-chat-AO__forward-toast" role="status">
                        <span>✓</span>
                        {forwardNotice}
                    </div>
                )}

                {createOpen && (
                    <div
                        className="chat-modal-AO"
                        onMouseDown={() => {
                            if (!createBusy) setCreateOpen(false);
                        }}
                    >
                        <div
                            className="chat-modal-AO__body office-chat-create-AO"
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <div className="chat-modal-AO__head">
                                <div>
                                    <div className="chat-modal-AO__title">Создать чат</div>
                                    <div className="chat-modal-AO__subtitle">Личный диалог или новая группа.</div>
                                </div>
                                <button type="button" onClick={() => setCreateOpen(false)} disabled={!!createBusy}>✕</button>
                            </div>

                            <div className="office-chat-create-AO__modes">
                                <button
                                    type="button"
                                    className={createMode === "direct" ? "is-active" : ""}
                                    onClick={() => {
                                        setCreateMode("direct");
                                        setCreateError("");
                                    }}
                                    disabled={!!createBusy}
                                >
                                    <span>Написать человеку</span>
                                    <small>Личный чат 1 на 1</small>
                                </button>
                                <button
                                    type="button"
                                    className={createMode === "group" ? "is-active" : ""}
                                    onClick={() => {
                                        setCreateMode("group");
                                        setCreateError("");
                                    }}
                                    disabled={!!createBusy}
                                >
                                    <span>Создать группу</span>
                                    <small>Несколько участников</small>
                                </button>
                            </div>

                            {createMode === "group" && (
                                <label className="office-chat-create-AO__field">
                                    <span>Название группы</span>
                                    <input
                                        value={createGroupTitle}
                                        onChange={(event) => {
                                            setCreateGroupTitle(event.target.value);
                                            setCreateError("");
                                        }}
                                        maxLength={120}
                                        placeholder="Например, Производство"
                                        disabled={!!createBusy}
                                    />
                                </label>
                            )}

                            <label className="office-chat-create-AO__search">
                                <SearchRoundedIcon sx={{ fontSize: 19 }} />
                                <input
                                    autoFocus
                                    type="search"
                                    value={createSearch}
                                    onChange={(event) => {
                                        setCreateSearch(event.target.value);
                                        setCreateError("");
                                    }}
                                    placeholder="Найти пользователя по имени или email"
                                    disabled={!!createBusy}
                                />
                                {!!createSearch && (
                                    <button type="button" onClick={() => setCreateSearch("")} disabled={!!createBusy}>
                                        <CloseRoundedIcon sx={{ fontSize: 17 }} />
                                    </button>
                                )}
                            </label>

                            <div className="office-chat-create-AO__contacts-head">
                                <div><b>Контакты</b><span>{createContacts.length}</span></div>
                            </div>

                            <div className="office-chat-create-AO__contacts">
                                {createContacts.map((contact) => {
                                    const id = getId(contact);
                                    const selected = createGroupIds.includes(id);
                                    const busy = createBusy === id;

                                    return (
                                        <button
                                            type="button"
                                            key={id}
                                            className={[
                                                "office-chat-create-AO__contact",
                                                selected ? "is-selected" : "",
                                                busy ? "is-busy" : "",
                                            ].filter(Boolean).join(" ")}
                                            disabled={!!createBusy && !busy}
                                            onClick={() => {
                                                if (createMode === "direct") {
                                                    openContactChat(contact);
                                                    return;
                                                }
                                                toggleGroupMember(id);
                                            }}
                                        >
                                            <span className="office-chat-create-AO__avatar">
                                                {getUserName(contact).slice(0, 1).toUpperCase()}
                                            </span>
                                            <span className="office-chat-create-AO__contact-main">
                                                <b>{getUserName(contact)}</b>
                                                <small>{[contact?.role, contact?.email].filter(Boolean).join(" · ")}</small>
                                            </span>
                                            {createMode === "direct" ? (
                                                <span className="office-chat-create-AO__contact-action">
                                                    {busy ? "Открываем…" : "Написать"}
                                                </span>
                                            ) : (
                                                <span className={`office-chat-create-AO__contact-check ${selected ? "is-selected" : ""}`}>
                                                    {selected ? "✓" : ""}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}

                                {!createContacts.length && (
                                    <div className="office-chat-create-AO__empty">Пользователей не найдено.</div>
                                )}
                            </div>

                            {!!createError && (
                                <div className="office-chat-create-AO__error" role="alert">{createError}</div>
                            )}

                            {createMode === "group" && (
                                <form className="office-chat-create-AO__actions" onSubmit={createGroup}>
                                    <span className="office-chat-create-AO__selected">
                                        Выбрано: <b>{createGroupIds.length}</b>
                                    </span>
                                    <button
                                        type="button"
                                        className="office-chat-AO__btn"
                                        onClick={() => setCreateOpen(false)}
                                        disabled={!!createBusy}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        className="office-chat-AO__btn office-chat-AO__btn--primary"
                                        disabled={!!createBusy || !createGroupTitle.trim() || !createGroupIds.length}
                                    >
                                        {createBusy === "group" ? "Создаём…" : "Создать группу"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                )}
            </div>
            </main>

            {overlaySection && overlayMeta && typeof document !== "undefined" && createPortal(
                <div
                    className="admin-messenger-modal-AO"
                    role="dialog"
                    aria-modal="true"
                    aria-label={overlayMeta.title}
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeOverlay();
                    }}
                >
                    <div className={`admin-messenger-modal-AO__dialog is-${overlaySection}`}>
                        <div className="admin-messenger-modal-AO__head">
                            <div className="admin-messenger-modal-AO__title-wrap">
                                <div className="admin-messenger-modal-AO__icon" aria-hidden="true">
                                    {overlaySection === "contacts"
                                        ? <ContactsOutlinedIcon sx={{ fontSize: 22 }} />
                                        : overlaySection === "calls"
                                            ? <PhoneOutlinedIcon sx={{ fontSize: 22 }} />
                                            : <SettingsOutlinedIcon sx={{ fontSize: 22 }} />}
                                </div>
                                <div>
                                    <div className="admin-messenger-modal-AO__title">
                                        {overlayMeta.title}
                                        {overlayMeta.count !== null && overlayMeta.count !== undefined && (
                                            <b>{overlayMeta.count}</b>
                                        )}
                                    </div>
                                    <div className="admin-messenger-modal-AO__subtitle">{overlayMeta.subtitle}</div>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="admin-messenger-modal-AO__close"
                                onClick={closeOverlay}
                                aria-label="Закрыть"
                                title="Закрыть"
                            >
                                <CloseRoundedIcon sx={{ fontSize: 22 }} />
                            </button>
                        </div>

                        {overlaySection !== "settings" && (
                            <label className="admin-messenger-modal-AO__search">
                                <SearchRoundedIcon sx={{ fontSize: 19 }} />
                                <input
                                    autoFocus
                                    type="search"
                                    value={overlaySearch}
                                    onChange={(event) => setOverlaySearch(event.target.value)}
                                    placeholder={overlaySection === "contacts" ? "Поиск контактов" : "Поиск по звонкам"}
                                    aria-label="Поиск"
                                />
                                {!!overlaySearch && (
                                    <button type="button" onClick={() => setOverlaySearch("")} aria-label="Очистить поиск">
                                        <CloseRoundedIcon sx={{ fontSize: 17 }} />
                                    </button>
                                )}
                            </label>
                        )}

                        <div className="admin-messenger-modal-AO__body">
                            {overlayContent}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
